import {
  pipe,
  not,
  empty,
  assoc,
  assocPath,
} from 'ramda';

const { Lurker } = window;

const getParam = (name) => {
  const pattern = new RegExp(`${name}=([0-9a-zA-Z_-]*)`, 'g');
  const param = document.location.search.match(pattern);
  return param ? param[0].split('=')[1] : '';
};

const addAdId = adId => pipe(assoc('ad_id', adId), assocPath(['data', 'ad_id'], adId));
const addListId = listId => pipe(assoc('list_id', listId), assocPath(['data', 'list_id'], listId));

export const sendEvent = (payload) => {
  const { referral } = payload;

  if (!referral) {
    const referralParam = getParam('referral');

    Lurker.send({ ...payload, referral: referralParam });
  } else {
    Lurker.send(payload);
  }
};

export const sendPageView = ({
  adId,
  listId,
  publicAccountId,
  categoryId,
  pointOfSale,
  referral,
  funnelId,
  productNames,
  productPrices,
  zipcode,
  region,
}) => sendEvent({
  event_type: 'page_view',
  object_detail: 'load',
  ad_id: adId,
  category_id: categoryId,
  zipcode,
  region_id: region,
  data: {
    ad_id: adId,
    list_id: listId,
    category_id: categoryId,
    public_account_id: publicAccountId,
    point_of_sale: (adId || listId) ? pointOfSale : `${pointOfSale}-standalone`,
    referral: not(empty(referral)) ? referral : '',
    funnel_id: funnelId,
    product_names: productNames,
    product_prices: productPrices,
  },
});

export const sendBuyClick = ({
  adId,
  listId,
  product,
  pointOfSale,
  referral,
  funnelId,
  categoryId,
  region,
  zipcode,
}) => {
  const objectDetail = pointOfSale.includes('subscription')
    ? 'subscription_buy'
    : 'payg_buy';
  const event = {
    event_type: 'click',
    object_detail: objectDetail,
    category_id: categoryId,
    region_id: region,
    zipcode,
    data: {
      point_of_sale: (adId || listId) ? pointOfSale : `${pointOfSale}-standalone`,
      funnel_id: funnelId,
      referral,
      product,
    },
  };

  sendEvent(pipe(addAdId(adId), addListId(listId))(event));
};

export const trackSwitchStep = ({
  adId, listId, pointOfSale, step, funnelId,
}) => sendEvent({
  event_type: 'click',
  object_detail: 'change_step',
  data: {
    point_of_sale: (adId || listId) 
    ? pointOfSale === 'autos-subscription-plans' ? 'cars-subscription-bundle' : pointOfSale
    : `${pointOfSale}-standalone`,
    step,
    funnel_id: funnelId,
  },
});

export const trackMyAdsClick = (adId, listId, pointOfSale, funnelId) => {
  const event = {
    event_type: 'click',
    object_detail: 'without_highlight',
    data: {
      point_of_sale: pointOfSale,
      funnel_id: funnelId,
    },
  };

  sendEvent(pipe(
    addAdId(adId),
    addListId(listId),
  )(event));
};

export const trackHeaderClick = (adId, listId, pointOfSale, btnName, funnelId) => {
  const event = {
    event_type: 'click',
    object_detail: 'header',
    data: {
      point_of_sale: (adId || listId) ? pointOfSale : `${pointOfSale}-standalone`,
      funnel_id: funnelId,
      btn: btnName,
    },
  };

  sendEvent(pipe(
    addAdId(adId),
    addListId(listId),
  )(event));
};

export const sendScroll = (pointOfSale, position, funnelId, categoryId, region, zipcode) => {
  const referral = getParam('referral');
  const adId = getParam('adId');
  const listId = getParam('listId');
  const event = {
    event_type: 'scroll',
    object_detail: 'highlight_scroll',
    category_id: categoryId,
    region_id: region,
    zipcode,
    data: {
      position,
      referral,
      point_of_sale: pointOfSale,
      funnel_id: funnelId,
    },
  };

  sendEvent(pipe(
    addAdId(adId),
    addListId(listId),
  )(event));
};

export const sendCharacteristicView = (pointOfSale, link, product, funnelId) => {
  const referral = getParam('referral');
  const adId = getParam('adId');
  const listId = getParam('listId');
  const event = {
    event_type: 'click',
    object_detail: 'view_characteristic',
    data: {
      link,
      product,
      referral,
      point_of_sale: pointOfSale,
      funnel_id: funnelId,
    },
  };

  sendEvent(pipe(
    addAdId(adId),
    addListId(listId),
  )(event));
};

export const sendBenefitView = (pointOfSale, direction, type, funnelId) => {
  const referral = getParam('referral');
  const adId = getParam('adId');
  const listId = getParam('listId');
  const event = {
    event_type: 'click',
    object_detail: 'view_benefit',
    data: {
      direction,
      referral,
      type,
      point_of_sale: pointOfSale,
      funnel_id: funnelId,
    },
  };

  sendEvent(pipe(
    addAdId(adId),
    addListId(listId),
  )(event));
};

export const sendCommunicationClick = (pointOfSale, type, funnelId) => {
  const referral = getParam('referral');
  const adId = getParam('adId');
  const listId = getParam('listId');

  const event = {
    event_type: 'click',
    object_detail: 'communication',
    data: {
      referral,
      type,
      point_of_sale: pointOfSale,
      funnel_id: funnelId,
    },
  };

  sendEvent(pipe(
    addAdId(adId),
    addListId(listId),
  )(event));
};

export const sendBenefitArrowClick = (pointOfSale, direction, funnelId) => sendBenefitView(pointOfSale, direction, 'btn', funnelId);
export const sendBenefitSwipe = (pointOfSale, direction, funnelId) => sendBenefitView(pointOfSale, direction, 'swipe', funnelId);

export const selectSubscriptionRange = ({
  adId, listId, pointOfSale, select, funnelId,
}) => sendEvent({
  event_type: 'click',
  object_detail: 'change_step',
  data: {
    point_of_sale: (adId || listId) ? pointOfSale : `${pointOfSale}-standalone`,
    funnel_id: funnelId,
    select,
  },
});

export const useDiscountClick = ({ pointOfSale, funnelId }) => {
  const referral = getParam('referral');
  const adId = getParam('adId');
  const listId = getParam('listId');

  const event = {
    event_type: 'click',
    object_detail: 'use_discount_modal',
    data: {
      point_of_sale: pointOfSale,
      funnel_id: funnelId,
      referral,
    },
  };

  sendEvent(pipe(
    addAdId(adId),
    addListId(listId),
  )(event));
};

export const discountModalView = ({ pointOfSale, funnelId }) => {
  const referral = getParam('referral');
  const adId = getParam('adId');
  const listId = getParam('listId');

  const event = {
    event_type: 'view',
    object_detail: 'modal_discount',
    data: {
      point_of_sale: pointOfSale,
      funnel_id: funnelId,
      referral,
    },
  };

  sendEvent(pipe(
    addAdId(adId),
    addListId(listId),
  )(event));
};

export const aboutLimitsModalView = ({ pointOfSale, funnelId }) => {
  const referral = getParam('referral');
  const adId = getParam('adId');
  const listId = getParam('listId');

  const event = {
    event_type: 'view',
    object_detail: 'modal_about_limits',
    data: {
      point_of_sale: pointOfSale === 'autos-subscription-plans' ? 'cars-subscription-bundle' : pointOfSale,
      funnel_id: funnelId,
      referral,
    },
  };

  sendEvent(pipe(
    addAdId(adId),
    addListId(listId),
  )(event));
};

export default {
  sendEvent,
  sendPageView,
  sendBuyClick,
  sendScroll,
  sendBenefitArrowClick,
  sendBenefitSwipe,
  selectSubscriptionRange,
  useDiscountClick,
  discountModalView,
};
